// ──────────────────────────────────────────────────────────────────────
// D59 — Live data hook.
// D84 — Subscribes to the workspace-wide WebSocket (via
// `WorkspaceLiveProvider`) by topic; refetches instantly on server-pushed
// invalidate events. Polling falls back to a 15s interval ONLY when the
// socket isn't live (disconnected / reconnecting / soft-fail).
//
// Drop-in replacement for the common `useEffect(() => { fetch(); }, [deps])`
// pattern. Adds:
//   1. Real-time push from any workspace member's mutation (D84) → instant
//      refetch when the matching topic fires.
//   2. Periodic polling fallback (15s) — kicks in only when the WS is
//      disconnected / reconnecting / has soft-failed to polling.
//   3. Refetch on window focus.
//   4. Refetch on a custom event name (legacy event-bus — still used by
//      a few same-tab flows).
//   5. Initial fetch on mount + on `deps` change.
//   6. Exposes `lastSynced` (Date | null) for the LastSynced pill.
//
// USAGE:
//   const { data, loading, error, lastSynced, refetch } = useLiveData(
//     async () => (await api.get('/eod/calendar?ws_id=…')).data,
//     [workspaceId, weekStart],
//     { intervalMs: 15_000, events: ['stellar-eod-changed'], topic: 'eod' },
//   );
// ──────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceLive } from './WorkspaceLiveProvider';

// Global event helper — anywhere in the app can call this after a mutation:
//   broadcastChange('stellar-eod-changed')
// and every `useLiveData` listening on that event will refetch.
export function broadcastChange(eventName, detail = null) {
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch (_e) { /* SSR / non-browser */ }
}

export default function useLiveData(fetcher, deps = [], options = {}) {
  const {
    intervalMs = 15_000,           // polling fallback interval
    refetchOnFocus = true,
    events = [],                   // legacy window event names
    enabled = true,
    initialData = null,
    topic = null,                  // D84 — workspace WS topic; null = no subscribe
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const inFlight = useRef(false);
  const fetcherRef = useRef(fetcher);
  // We read the live context unconditionally to keep hook order stable.
  // When no provider is mounted upstream the default `disconnected` value
  // is returned and the polling path is used.
  const { connectionState, subscribe } = useWorkspaceLive();

  // Always call the latest closure so callers don't need useCallback.
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    if (inFlight.current) return; // dedupe overlapping
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await fetcherRef.current();
      setData(result);
      setError(null);
      setLastSynced(new Date());
    } catch (err) {
      setError(err);
      // Don't blow away the existing `data` — keep showing the last good value.
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Initial fetch + on deps change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, deps);

  // D84 — Real-time push: subscribe by topic if the consumer asked for one.
  useEffect(() => {
    if (!enabled || !topic) return undefined;
    return subscribe(topic, () => { refetch(); });
  }, [enabled, topic, subscribe, refetch]);

  // Polling — but ONLY when the live socket isn't carrying our events.
  // When connectionState === 'live' we suppress the interval entirely so
  // we don't double-fetch (the WS will push when there's an actual change).
  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;
    if (topic && connectionState === 'live') return undefined;  // WS is doing the work
    const id = setInterval(refetch, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, refetch, topic, connectionState]);

  // Focus refetch.
  useEffect(() => {
    if (!enabled || !refetchOnFocus) return undefined;
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, refetchOnFocus, refetch]);

  // Custom event refetch (legacy in-tab event bus).
  useEffect(() => {
    if (!enabled || events.length === 0) return undefined;
    const handlers = events.map(name => {
      const h = () => refetch();
      window.addEventListener(name, h);
      return [name, h];
    });
    return () => handlers.forEach(([name, h]) => window.removeEventListener(name, h));
  }, [enabled, events, refetch]);

  return { data, loading, error, lastSynced, refetch };
}

// ──────────────────────────────────────────────────────────────────────
// D84 — Lightweight topic subscription for pages that DON'T use
// useLiveData (their data load lives in a useEffect/useState pair).
// On a server-pushed `invalidate` for `topic`, calls `onInvalidate()`.
// Falls back to nothing when no provider is mounted — caller's existing
// effect/poll keeps working.
//
// USAGE inside a page:
//   const load = useCallback(async () => { ... }, [...]);
//   useEffect(() => { load(); }, [load]);
//   useLiveInvalidate('tasks', load);
// ──────────────────────────────────────────────────────────────────────
export function useLiveInvalidate(topic, onInvalidate) {
  const { subscribe } = useWorkspaceLive();
  const fnRef = useRef(onInvalidate);
  useEffect(() => { fnRef.current = onInvalidate; }, [onInvalidate]);
  useEffect(() => {
    if (!topic) return undefined;
    return subscribe(topic, () => {
      try { fnRef.current?.(); } catch { /* swallow */ }
    });
  }, [topic, subscribe]);
}
